import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { SelectionModel } from '@angular/cdk/collections';
import { ConnectedPosition, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { TAB } from '@angular/cdk/keycodes';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  InjectionToken,
  input,
  Input,
  InputSignal,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { SimIconType } from '@simOn/ui/sim-icon';
import { take, takeUntil } from 'rxjs/operators';
import { selectAnimations } from '../animations/sim-select.animations';
import { _SelectBase } from '../directives/select-base.directive';
import { MAT_OPTION_PARENT_COMPONENT } from '../interfaces/options-parent';
import { OptionComponent } from './option/option.component';

export const HEIGHT_BREAK = 5;
export const SELECT_ITEM_HEIGHT_EM = 3;
export const SELECT_PANEL_MAX_HEIGHT = 256;
/** The panel's padding on the x-axis. */
export const SELECT_PANEL_PADDING_X = 16;
/** The panel's x axis padding if it is indented (e.g. there is an option group). */
export const SELECT_PANEL_INDENT_PADDING_X = SELECT_PANEL_PADDING_X * 2;
export const SELECT_PANEL_VIEWPORT_PADDING = 8;
/** Injection token that determines the scroll handling while a select is open. */
export const MAT_SELECT_SCROLL_STRATEGY = new InjectionToken<() => ScrollStrategy>('mat-select-scroll-strategy');
export const SELECT_MULTIPLE_PANEL_PADDING_X = SELECT_PANEL_PADDING_X * 1.5 + 16;

/** @docs-private */
export function MAT_SELECT_SCROLL_STRATEGY_PROVIDER_FACTORY(overlay: Overlay): () => ScrollStrategy {
  return () => overlay.scrollStrategies.block();
}
export const MAT_SELECT_SCROLL_STRATEGY_PROVIDER = {
  provide: MAT_SELECT_SCROLL_STRATEGY,
  deps: [Overlay],
  useFactory: MAT_SELECT_SCROLL_STRATEGY_PROVIDER_FACTORY
};
@Component({
    selector: 'sim-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    // eslint-disable-next-line @angular-eslint/no-host-metadata-property
    host: {
        role: 'combobox',
        'aria-autocomplete': 'none',
        'aria-haspopup': 'true',
        tabindex: '0',
        class: 'sim-select',
        '[class.select-readonly]': 'readonly()',
        '[class.sim-select-disabled]': 'disabled',
        '[class.sim-select-invalid]': 'errorState',
        '[class.sim-select-required]': 'required',
        '[class.sim-select-empty]': 'empty',
        '[class.sim-select-multiple]': 'multiple',
        '(keydown)': '_handleKeydown($event)',
        '(focus)': '_onFocus()',
        '(blur)': '_onBlur()',
        '[id]': 'id',
        '[class.status-select]': 'selectStatus()'
    },
    animations: [selectAnimations.transformPanelWrap, selectAnimations.transformPanel],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: MAT_OPTION_PARENT_COMPONENT, useExisting: SelectComponent }],
    standalone: false
})
export class SelectComponent extends _SelectBase<SelectChange> implements OnInit, AfterViewInit {
  @ViewChild('searchInput', { read: ElementRef }) private _searchInputRef?: ElementRef<HTMLElement>;
  private _showSearchInput: boolean = false;
  private _scrollTop = 0;
  @Output() inputValueChanges: EventEmitter<string> = new EventEmitter<string>();

  @Input() set showSearchInput(value: BooleanInput) {
    this._showSearchInput = coerceBooleanProperty(value);
  }
  get showSearchInput(): boolean {
    return this._showSearchInput;
  }

  readonly suffix: InputSignal<TemplateRef<any> | undefined> = input<TemplateRef<any>>();

  readonly prefix: InputSignal<TemplateRef<any> | undefined> = input<TemplateRef<any>>();

  @Input() override disabled: boolean = false;

  @Input() inputValue: string = '';

  @Input() iconUrl!: SimIconType;

  @Input() iconColor!: string;

  @Input() customOverlayClass = '';

  /** When enabled, the overlay panel is pushed back on-screen instead of overflowing the viewport. */
  @Input() overlayPush = false;

  /** Minimal distance between the overlay panel and the viewport edge when `overlayPush` is enabled. */
  @Input() overlayViewportMargin = 0;

  @Input() panelMaxHeight?: string;

  @HostBinding('class.country-not-selected') get disabledButtonClass(): boolean {
    return this.changeColorNotSelectedAnyElement;
  }
  @Input() changeColorNotSelectedAnyElement: boolean = false;

  @Input() arrowColor: string = '#596870';

  @ContentChildren(OptionComponent, { descendants: true })
  options!: QueryList<OptionComponent>;
  _isOpen = false;
  customTrigger = null;

  _triggerRect!: DOMRect;
  _triggerFontSize = 0;
  _transformOrigin = 'top';
  _overlayPanelClass: string | string[] = '';
  _positions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 5
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'bottom'
    }
  ];
  override _selectionModel!: SelectionModel<OptionComponent>;

  _offsetY = 0;

  emitInputValueChanges(event: string | number): void {
    this.inputValueChanges.emit(event.toString());
  }

  /** Internal API for `SelectSearchDirective` — drives the panel search input of this OnPush host. */
  _setSearchState(showSearchInput: boolean, inputValue: string): void {
    this._showSearchInput = showSearchInput;
    this.inputValue = inputValue;
    this._changeDetectorRef.markForCheck();
  }

  /** Shift+Tab from the search input goes back onto the trigger, keeping the panel open. */
  _handleSearchKeydown(event: KeyboardEvent): void {
    if (event.keyCode === TAB && event.shiftKey) {
      event.preventDefault();
      this.focus();
    }
  }

  ngAfterViewInit(): void {
    this._triggerRect = this.trigger.nativeElement.getBoundingClientRect();
  }
  _calculateOverlayScroll(selectedIndex: number, scrollBuffer: number, maxScroll: number): number {
    const itemHeight = this._getItemHeight();
    const optionOffsetFromScrollTop = itemHeight * selectedIndex;
    const halfOptionHeight = itemHeight / 2;

    // Starts at the optionOffsetFromScrollTop, which scrolls the option to the top of the
    // scroll container, then subtracts the scroll buffer to scroll the option down to
    // the center of the overlay panel. Half the option height must be re-added to the
    // scrollTop so the option is centered based on its middle, not its top edge.
    const optimalScrollPosition = optionOffsetFromScrollTop - scrollBuffer + halfOptionHeight;
    return Math.min(Math.max(0, optimalScrollPosition), maxScroll);
  }
  override open(): void {
    if (super._canOpen()) {
      super.open();
      this._triggerRect = this.trigger.nativeElement.getBoundingClientRect();
      // Note: The computed font-size will be a string pixel value (e.g. "16px").
      // `parseInt` ignores the trailing 'px' and converts this to a number.
      this._triggerFontSize = parseInt(getComputedStyle(this.trigger.nativeElement).fontSize || '0');
      this._calculateOverlayPosition();

      // Set the font size on the panel element once it exists.
      this._ngZone.onStable.pipe(take(1)).subscribe(() => {
        if (this._triggerFontSize && this._overlayDir.overlayRef && this._overlayDir.overlayRef.overlayElement) {
          this._overlayDir.overlayRef.overlayElement.style.fontSize = `${this._triggerFontSize}px`;
        }
      });
    }
  }
  override ngOnInit() {
    super.ngOnInit();
    this._viewportRuler
      .change()
      .pipe(takeUntil(this._destroy))
      .subscribe(() => {
        if (this.panelOpen) {
          this._triggerRect = this.trigger.nativeElement.getBoundingClientRect();
          this._changeDetectorRef.markForCheck();
        }
      });
  }
  protected _getChangeEvent(value: any) {
    return new SelectChange(this, value);
  }

  protected override _getPanelFirstFocusable(): HTMLElement | null {
    const searchInput = this._searchInputRef?.nativeElement.querySelector('input');
    return searchInput ?? super._getPanelFirstFocusable();
  }
  protected _scrollOptionIntoView(index: number): void {
    const labelCount = 0;
    const itemHeight = this._getItemHeight();

    this.panel.nativeElement.scrollTop = _getOptionScrollPosition(
      (index + labelCount) * itemHeight,
      itemHeight,
      this.panel.nativeElement.scrollTop,
      SELECT_PANEL_MAX_HEIGHT
    );
  }
  protected _positioningSettled() {
    // this._calculateOverlayOffsetX();
    // this.panel.nativeElement.scrollTop = this._scrollTop;
  }
  private _calculateOverlayOffsetX(): void {
    const overlayRect = this._overlayDir.overlayRef.overlayElement.getBoundingClientRect();
    const viewportSize = this._viewportRuler.getViewportSize();
    const isRtl = this._isRtl();
    const paddingWidth = this.multiple
      ? SELECT_MULTIPLE_PANEL_PADDING_X + SELECT_PANEL_PADDING_X
      : SELECT_PANEL_PADDING_X * 2;
    let offsetX: number;

    // Adjust the offset, depending on the option padding.
    if (this.multiple) {
      offsetX = SELECT_MULTIPLE_PANEL_PADDING_X;
    } else {
      const selected = this._selectionModel.selected[0] || this.options.first;
      offsetX = SELECT_PANEL_PADDING_X;
    }

    // Invert the offset in LTR.
    if (!isRtl) {
      offsetX *= -1;
    }

    // Determine how much the select overflows on each side.
    const leftOverflow = 0 - (overlayRect.left + offsetX - (isRtl ? paddingWidth : 0));
    const rightOverflow = overlayRect.right + offsetX - viewportSize.width + (isRtl ? 0 : paddingWidth);

    // If the element overflows on either side, reduce the offset to allow it to fit.
    if (leftOverflow > 0) {
      offsetX += leftOverflow + SELECT_PANEL_VIEWPORT_PADDING;
    } else if (rightOverflow > 0) {
      offsetX -= rightOverflow + SELECT_PANEL_VIEWPORT_PADDING;
    }

    // Set the offset directly in order to avoid having to go through change detection and
    // potentially triggering "changed after it was checked" errors. Round the value to avoid
    // blurry content in some browsers.
    this._overlayDir.offsetX = Math.round(offsetX);
    this._overlayDir.overlayRef.updatePosition();
  }

  private _getItemHeight(): number {
    return this._triggerFontSize * SELECT_ITEM_HEIGHT_EM;
  }

  /** Calculates the amount of items in the select. This includes options and group labels. */
  private _getItemCount(): number {
    return this.options.length;
  }
  private _calculateOverlayPosition(): void {
    const itemHeight = this._getItemHeight();
    const items = this._getItemCount();
    const panelHeight = Math.min(items * itemHeight, SELECT_PANEL_MAX_HEIGHT);
    const scrollContainerHeight = items * itemHeight;

    // The farthest the panel can be scrolled before it hits the bottom
    const maxScroll = scrollContainerHeight - panelHeight;

    // If no value is selected we open the popup to the first item.
    let selectedOptionOffset: number;

    if (this.empty) {
      selectedOptionOffset = 0;
    } else {
      selectedOptionOffset = Math.max(this.options.toArray().indexOf(this._selectionModel.selected[0]), 0);
    }

    //TODO what ????
    // selectedOptionOffset += _countGroupLabelsBeforeOption(
    //   selectedOptionOffset,
    //   this.options,
    //   this.optionGroups
    // );

    // We must maintain a scroll buffer so the selected option will be scrolled to the
    // center of the overlay panel rather than the top.
    const scrollBuffer = panelHeight / 2;
    this._scrollTop = this._calculateOverlayScroll(selectedOptionOffset, scrollBuffer, maxScroll);
    this._offsetY = this._triggerRect.height + HEIGHT_BREAK;

    this._checkOverlayWithinViewport(maxScroll);
  }
  private _checkOverlayWithinViewport(maxScroll: number): void {
    const itemHeight = this._getItemHeight();
    const viewportSize = this._viewportRuler.getViewportSize();

    const topSpaceAvailable = this._triggerRect.top - SELECT_PANEL_VIEWPORT_PADDING;
    const bottomSpaceAvailable = viewportSize.height - this._triggerRect.bottom - SELECT_PANEL_VIEWPORT_PADDING;

    const panelHeightTop = Math.abs(this._offsetY);
    const totalPanelHeight = Math.min(this._getItemCount() * itemHeight, SELECT_PANEL_MAX_HEIGHT);
    const panelHeightBottom = totalPanelHeight - panelHeightTop - this._triggerRect.height;

    if (panelHeightBottom > bottomSpaceAvailable) {
      this._adjustPanelUp(panelHeightBottom, bottomSpaceAvailable);
    } else if (panelHeightTop > topSpaceAvailable) {
      this._adjustPanelDown(panelHeightTop, topSpaceAvailable, maxScroll);
    } else {
      this._transformOrigin = this._getOriginBasedOnOption();
    }
  }
  private _getOriginBasedOnOption(): string {
    const itemHeight = this._getItemHeight();
    const optionHeightAdjustment = (itemHeight - this._triggerRect.height) / 2;
    const originY = Math.abs(this._offsetY) - optionHeightAdjustment + itemHeight / 2;
    return `50% ${originY}px 0px`;
  }

  /** Adjusts the overlay panel up to fit in the viewport. */
  private _adjustPanelUp(panelHeightBottom: number, bottomSpaceAvailable: number) {
    // Browsers ignore fractional scroll offsets, so we need to round.
    const distanceBelowViewport = Math.round(panelHeightBottom - bottomSpaceAvailable);

    // Scrolls the panel up by the distance it was extending past the boundary, then
    // adjusts the offset by that amount to move the panel up into the viewport.
    this._scrollTop -= distanceBelowViewport;
    this._offsetY -= distanceBelowViewport;
    this._transformOrigin = this._getOriginBasedOnOption();

    // If the panel is scrolled to the very top, it won't be able to fit the panel
    // by scrolling, so set the offset to 0 to allow the fallback position to take
    // effect.
    if (this._scrollTop <= 0) {
      this._scrollTop = 0;
      this._offsetY = 0;
      this._transformOrigin = `50% bottom 0px`;
    }
  }
  /** Adjusts the overlay panel down to fit in the viewport. */
  private _adjustPanelDown(panelHeightTop: number, topSpaceAvailable: number, maxScroll: number) {
    // Browsers ignore fractional scroll offsets, so we need to round.
    const distanceAboveViewport = Math.round(panelHeightTop - topSpaceAvailable);

    // Scrolls the panel down by the distance it was extending past the boundary, then
    // adjusts the offset by that amount to move the panel down into the viewport.
    this._scrollTop += distanceAboveViewport;
    this._offsetY += distanceAboveViewport;
    this._transformOrigin = this._getOriginBasedOnOption();

    // If the panel is scrolled to the very bottom, it won't be able to fit the
    // panel by scrolling, so set the offset to 0 to allow the fallback position
    // to take effect.
    if (this._scrollTop >= maxScroll) {
      this._scrollTop = maxScroll;
      this._offsetY = 0;
      this._transformOrigin = `50% top 0px`;
      return;
    }
  }

  override get empty(): boolean {
    return !this._selectionModel || this._selectionModel.isEmpty();
  }
}

export class SelectChange {
  constructor(
    /** Reference to the select that emitted the change event. */
    public source: SelectComponent,
    /** Current value of the select that emitted the event. */
    public value: any
  ) {}
}
export function _getOptionScrollPosition(
  optionOffset: number,
  optionHeight: number,
  currentScrollPosition: number,
  panelHeight: number
): number {
  if (optionOffset < currentScrollPosition) {
    return optionOffset;
  }

  if (optionOffset + optionHeight > currentScrollPosition + panelHeight) {
    return Math.max(0, optionOffset - panelHeight + optionHeight);
  }

  return currentScrollPosition;
}
